import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NEAR_END_THRESHOLD,
  SessionProgress,
  SessionStatus,
  clearSessionProgress,
  readSessionProgress,
  writeSessionProgress,
} from '@/utils/progressStore';

const SAVE_INTERVAL_MS = 5_000;

/**
 * Per-(viewer, session) playback hook backed by the shared progress store.
 * Exposes:
 *   - <c>resumeAt</c>: position to seek to on metadata load (0 if completed
 *     or absent).
 *   - <c>status</c>: 'not-started' | 'in-progress' | 'completed'.
 *   - <c>save(position, duration)</c>: throttled write — drives course-level
 *     progress badges by flipping status to "in-progress" the first time
 *     playback advances past the very start.
 *   - <c>markComplete(duration)</c>: sets status='completed' and clears the
 *     resume position. Called when the player reports completion.
 *   - <c>clear()</c>: removes the entry entirely (used when wiping notes).
 *
 * Owner key (anonymousId vs. userId) is resolved per-call so signing in
 * picks up the migrated user blob without a remount.
 */
export function useSessionResume(sessionId: number | null | undefined, userId?: string | null) {
  const [resumeAt, setResumeAt] = useState<number>(0);
  const [status, setStatus] = useState<SessionStatus>('not-started');
  const lastSavedAtRef = useRef<number>(0);

  // Reload whenever the session or owner identity changes.
  useEffect(() => {
    if (!sessionId) {
      setResumeAt(0);
      setStatus('not-started');
      return;
    }
    const entry = readSessionProgress(userId, sessionId);
    if (!entry) {
      setResumeAt(0);
      setStatus('not-started');
      return;
    }
    if (entry.status === 'completed') {
      setResumeAt(0);
      setStatus('completed');
      return;
    }
    const finished = entry.duration > 0 && entry.position >= entry.duration - NEAR_END_THRESHOLD;
    setResumeAt(finished ? 0 : entry.position);
    setStatus(finished ? 'completed' : entry.status);
  }, [sessionId, userId]);

  const save = useCallback(
    (position: number, duration: number) => {
      if (!sessionId || !Number.isFinite(position) || position < 0) return;
      const now = Date.now();
      if (now - lastSavedAtRef.current < SAVE_INTERVAL_MS) return;
      lastSavedAtRef.current = now;
      const next = writeSessionProgress(userId, sessionId, {
        position,
        duration: duration || 0,
        status: position > 1 ? 'in-progress' : 'not-started',
      });
      setStatus(next.status);
    },
    [sessionId, userId],
  );

  const markComplete = useCallback(
    (duration?: number) => {
      if (!sessionId) return;
      const next: Partial<SessionProgress> = {
        status: 'completed',
        position: 0,
        duration: duration && duration > 0 ? duration : undefined,
        completedAt: Date.now(),
      };
      writeSessionProgress(userId, sessionId, next);
      lastSavedAtRef.current = 0;
      setResumeAt(0);
      setStatus('completed');
    },
    [sessionId, userId],
  );

  const clear = useCallback(() => {
    if (!sessionId) return;
    clearSessionProgress(userId, sessionId);
    lastSavedAtRef.current = 0;
    setResumeAt(0);
    setStatus('not-started');
  }, [sessionId, userId]);

  return { resumeAt, status, save, markComplete, clear };
}
